import React, { useState, useEffect } from 'react';
import { Activity, ActivityType } from '../../../types/Activity';
import { areasService } from '../../../services/areasService';
import { ServiceArea } from '../../../types/ForemanArea';
import { usePermissions } from '../../../hooks/usePermissions';
import { Form, Table, Alert, Button, Row, Col, Modal } from '@/components/ui';

const getEmptyFormState = (): Partial<Activity> => ({
  activityName: '',
  unitOfMeasure: '',
  activityNumber: '',
  scheduled2Rate: null,
  serviceAreaId: null,
  contractYear: 6, // Default to 6 instead of current year
  classification: '',
  locationCode: '',
  activityType: '',
  point: false,
  linear: false,
  activityProfile: '',
  threshold: null,
});

interface ActivityModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (activity: Partial<Activity>) => Promise<void>;
  activity?: Activity;
}

const ActivityModal: React.FC<ActivityModalProps> = ({
  visible,
  onClose,
  onSave,
  activity
}) => {
  const [formData, setFormData] = useState<Partial<Activity>>(getEmptyFormState());
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'costing'>('details');
  const [financeActivityNumber, setFinanceActivityNumber] = useState<string>('');
  const [activityCostings, setActivityCostings] = useState<Array<{
    id?: number;
    serviceAreaId: number;
    contractYear: number;
    scheduled2Rate: number | null;
    serviceArea?: ServiceArea | null;
  }>>([]);
  const [newCosting, setNewCosting] = useState<{
    serviceAreaId: number | null;
    contractYear: number;
    scheduled2Rate: number | null;
  }>({
    serviceAreaId: null,
    contractYear: 6,
    scheduled2Rate: null,
  });
  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission('activity:edit');

  useEffect(() => {
    if (visible) {
      fetchServiceAreas();
    }
  }, [visible]);

  useEffect(() => {
    if (activity) {
      setFormData({
        ...getEmptyFormState(),
        ...activity,
        point: activity.point ?? false,
        linear: activity.linear ?? false,
        activityProfile: activity.activityProfile ?? '',
        activityType: activity.activityType ?? '',
      });
      // Load activity costings
      if (activity.activityCostings && activity.activityCostings.length > 0) {
        setActivityCostings(activity.activityCostings.map(costing => ({
          id: costing.id,
          serviceAreaId: costing.serviceAreaId,
          contractYear: costing.contractYear ?? 6,
          scheduled2Rate: costing.scheduled2Rate,
          serviceArea: costing.serviceArea,
        })));
      } else {
        setActivityCostings([]);
      }
      // TODO: Load financeActivityNumber if it exists in the activity
      // This may need to be added to the Activity type/interface
    } else {
      setFormData(getEmptyFormState());
      setFinanceActivityNumber('');
      setActivityCostings([]);
    }
  }, [activity]);

  const fetchServiceAreas = async () => {
    try {
      const areas = await areasService.getServiceAreas();
      setServiceAreas(areas as ServiceArea[]);
    } catch (error) {
      console.error('Error fetching service areas:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!isReadOnly) {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      
      if (name === 'financeActivityNumber') {
        setFinanceActivityNumber(value);
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]:
          name === 'scheduled2Rate' || name === 'serviceAreaId' || name === 'contractYear' || name === 'threshold'
            ? (value === '' ? null : Number(value))
            : type === 'checkbox'
            ? checked
            : value
      }));
    }
  };

  const handleAddCosting = () => {
    if (!newCosting.serviceAreaId || !newCosting.contractYear) {
      alert('Please select Service Area and Contract Year');
      return;
    }
    
    // Check if this combination already exists
    const exists = activityCostings.some(
      c => c.serviceAreaId === newCosting.serviceAreaId && 
           c.contractYear === newCosting.contractYear
    );
    
    if (exists) {
      alert('A costing for this Service Area and Contract Year combination already exists');
      return;
    }
    
    const serviceArea = serviceAreas.find(a => a.id === newCosting.serviceAreaId);
    setActivityCostings([...activityCostings, {
      ...newCosting,
      serviceAreaId: newCosting.serviceAreaId!,
      serviceArea: serviceArea || null,
    }]);
    
    // Reset new costing form
    setNewCosting({
      serviceAreaId: null,
      contractYear: 6,
      scheduled2Rate: null,
    });
  };

  const handleRemoveCosting = (index: number) => {
    setActivityCostings(activityCostings.filter((_, i) => i !== index));
  };

  const handleUpdateCosting = (index: number, field: string, value: any) => {
    const updated = [...activityCostings];
    updated[index] = { ...updated[index], [field]: value };
    setActivityCostings(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.activityName || !formData.unitOfMeasure || !formData.activityNumber || 
        !formData.classification) {
      alert('Please fill in all required fields: Activity Name, Unit of Measure, Activity Number, and Classification.');
      return;
    }
    
    // Validate that at least one costing exists
    if (activityCostings.length === 0) {
      alert('Please add at least one Activity Costing (Service Area, Contract Year, and Scheduled Rate).');
      return;
    }
    
    try {
      setLoading(true);
      // Include activity costings in the data
      // Remove serviceAreaId, contractYear, scheduled2Rate from formData as they're now in costings
      const { serviceAreaId, contractYear, scheduled2Rate, ...activityDetails } = formData;
      const dataToSave = {
        ...activityDetails,
        activityCostings: activityCostings.map(c => {
          const costing: any = {
            serviceAreaId: c.serviceAreaId,
            contractYear: c.contractYear,
            scheduled2Rate: c.scheduled2Rate,
          };
          // Only include id if it's an existing costing (id > 0)
          if (c.id && c.id > 0) {
            costing.id = c.id;
          }
          return costing;
        }),
        // financeActivityNumber would need to be added to the Activity type/backend
      } as Partial<Activity>;
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error saving activity:', error);
      alert('Failed to save activity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={visible} onHide={onClose} size="xl" backdrop={loading ? 'static' : true}>
      <Modal.Header closeButton>
        <Modal.Title>{activity ? 'Edit Activity' : 'Create Activity'}</Modal.Title>
      </Modal.Header>
      {/* Form wraps Body+Footer (footer holds the submit) and is the flex column itself. */}
      <form onSubmit={handleSubmit} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
            <Modal.Body>
              {/* Tab Navigation */}
              <ul className="tw-mb-3 tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-line tw-list-none tw-p-0" role="tablist">
                <li role="presentation">
                  <button
                    className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'details' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                    type="button"
                    onClick={() => setActiveTab('details')}
                  >
                    Activity Details <span className="tw-text-danger">*</span>
                  </button>
                </li>
                <li role="presentation">
                  <button
                    className={`-tw-mb-px tw-border-b-2 tw-px-3 tw-py-2 ${activeTab === 'costing' ? 'tw-border-primary tw-font-medium tw-text-primary' : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'}`}
                    type="button"
                    onClick={() => setActiveTab('costing')}
                  >
                    Activity Costing <span className="tw-text-danger">*</span>
                  </button>
                </li>
              </ul>

              {/* Tab Content */}
              <div>
                {/* Activity Details Tab */}
                {activeTab === 'details' && (
                  <div>
                    <div className="tw-mb-4">
                      <Form.Label>Activity Name <span className="tw-text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="activityName"
                        value={formData.activityName || ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        required
                      />
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Activity Number <span className="tw-text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="activityNumber"
                        value={formData.activityNumber || ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        required
                      />
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Unit of Measure <span className="tw-text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="unitOfMeasure"
                        value={formData.unitOfMeasure || ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        required
                      />
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Classification <span className="tw-text-danger">*</span></Form.Label>
                      <Form.Select
                        name="classification"
                        value={formData.classification || ''}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        required
                      >
                        <option value="">Select Classification</option>
                        {Object.values(ActivityType).map((type) => (
                          <option key={type} value={type.toLowerCase()}>{type}</option>
                        ))}
                      </Form.Select>
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Activity Type</Form.Label>
                      <Form.Select
                        name="activityType"
                        value={formData.activityType || ''}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        <option value="">Select Activity Type</option>
                        <option value="Routine">Routine</option>
                        <option value="Quantified">Quantified</option>
                      </Form.Select>
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Finance Activity Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="financeActivityNumber"
                        value={financeActivityNumber}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        placeholder="Enter finance activity number"
                      />
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Location Code</Form.Label>
                      <Form.Select
                        name="locationCode"
                        value={formData.locationCode || ''}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        <option value="">Select Location Code</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                      </Form.Select>
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Profile</Form.Label>
                      <Form.Control
                        type="text"
                        name="activityProfile"
                        value={formData.activityProfile || ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        placeholder="Enter profile"
                      />
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Threshold</Form.Label>
                      <Form.Control
                        type="number"
                        name="threshold"
                        value={formData.threshold ?? ''}
                        onChange={handleChange}
                        readOnly={isReadOnly}
                        step="0.01"
                        placeholder="Optional quantity limit"
                      />
                    </div>
                    <div className="tw-mb-4">
                      <Form.Label>Linear/Point</Form.Label>
                      <Form.Select
                        name="linearPoint"
                        value={
                          formData.linear ? 'linear' : 
                          formData.point ? 'point' : 
                          ''
                        }
                        onChange={(e) => {
                          if (!isReadOnly) {
                            const value = e.target.value;
                            setFormData(prev => ({
                              ...prev,
                              linear: value === 'linear',
                              point: value === 'point'
                            }));
                          }
                        }}
                        disabled={isReadOnly}
                      >
                        <option value="">Select option</option>
                        <option value="linear">Linear</option>
                        <option value="point">Point</option>
                      </Form.Select>
                    </div>
                  </div>
                )}

                {/* Activity Costing Tab */}
                {activeTab === 'costing' && (
                  <div>
                    <h6 className="tw-mb-3">Existing Activity Costings</h6>
                    {activityCostings.length > 0 ? (
                      <div className="tw-overflow-x-auto tw-mb-4">
                        <Table bordered size="sm">
                          <thead>
                            <tr>
                              <th>Service Area</th>
                              <th>Contract Year</th>
                              <th>Scheduled Rate</th>
                              {!isReadOnly && <th>Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {activityCostings.map((costing, index) => (
                              <tr key={index}>
                                <td>
                                  {costing.serviceArea?.name || 
                                   serviceAreas.find(a => a.id === costing.serviceAreaId)?.name || 
                                   `Area ${costing.serviceAreaId}`}
                                </td>
                                <td>{costing.contractYear}</td>
                                <td>
                                  {isReadOnly ? (
                                    costing.scheduled2Rate?.toFixed(2) || '0.00'
                                  ) : (
                                    <Form.Control
                                      type="number"
                                      size="sm"
                                      value={costing.scheduled2Rate ?? ''}
                                      onChange={(e) => handleUpdateCosting(
                                        index,
                                        'scheduled2Rate',
                                        e.target.value === '' ? null : parseFloat(e.target.value)
                                      )}
                                      step="0.01"
                                      placeholder="0.00"
                                    />
                                  )}
                                </td>
                                {!isReadOnly && (
                                  <td>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="danger"
                                      onClick={() => handleRemoveCosting(index)}
                                    >
                                      Remove
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert variant="info" className="tw-mb-4">
                        No activity costings added yet. Add one below.
                      </Alert>
                    )}

                    {!isReadOnly && (
                      <>
                        <hr className="tw-my-4 tw-border-t tw-border-line" />
                        <h6 className="tw-mb-3">Add New Activity Costing</h6>
                        <Row>
                          <Col md={3}>
                            <Form.Label>Contract Year <span className="tw-text-danger">*</span></Form.Label>
                            <Form.Select
                              value={newCosting.contractYear}
                              onChange={(e) => setNewCosting({
                                ...newCosting,
                                contractYear: parseInt(e.target.value)
                              })}
                            >
                              <option value="6">6</option>
                              <option value="7">7</option>
                              <option value="8">8</option>
                            </Form.Select>
                          </Col>
                          <Col md={4}>
                            <Form.Label>Service Area <span className="tw-text-danger">*</span></Form.Label>
                            <Form.Select
                              value={newCosting.serviceAreaId ?? ''}
                              onChange={(e) => setNewCosting({
                                ...newCosting,
                                serviceAreaId: e.target.value === '' ? null : parseInt(e.target.value)
                              })}
                            >
                              <option value="">Select Service Area</option>
                              {serviceAreas
                                .filter(area =>
                                  !activityCostings.some(
                                    c => c.serviceAreaId === area.id &&
                                         c.contractYear === newCosting.contractYear
                                  )
                                )
                                .map(area => (
                                  <option key={area.id} value={area.id.toString()}>
                                    {area.name}
                                  </option>
                                ))}
                            </Form.Select>
                          </Col>
                          <Col md={3}>
                            <Form.Label>Scheduled Rate</Form.Label>
                            <Form.Control
                              type="number"
                              value={newCosting.scheduled2Rate ?? ''}
                              onChange={(e) => setNewCosting({
                                ...newCosting,
                                scheduled2Rate: e.target.value === '' ? null : parseFloat(e.target.value)
                              })}
                              step="0.01"
                              placeholder="0.00"
                            />
                          </Col>
                          <Col md={2} className="tw-flex tw-items-end">
                            <Button
                              type="button"
                              variant="primary"
                              onClick={handleAddCosting}
                            >
                              Add Costing
                            </Button>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              {!isReadOnly && (
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (activity ? 'Update' : 'Create')}
                </Button>
              )}
            </Modal.Footer>
          </form>
    </Modal>
  );
};

export default ActivityModal; 